import { useEffect, useState, useCallback } from "react";
import api from "../lib/api";
import { StatusBadge } from "./Badges";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { toast } from "sonner";
import { History as HistoryIcon, Link2, FileText, Radar, Loader2, AlertTriangle } from "lucide-react";

const STATUSES = ["CANONICAL", "WORKING", "OPEN", "HYPOTHESIS", "SUPERSEDED", "HISTORICAL"];
const RADAR_TYPES = ["CONSTITUTION", "DECISION"];

const REL = {
  CONTRADICTION: { cls: "bg-red-50 text-red-700 border-red-200", label: "CONTRADICTION" },
  POSSIBLE_SUPERSESSION: { cls: "bg-amber-50 text-amber-700 border-amber-200", label: "POSSIBLE SUPERSESSION" },
  REVISION: { cls: "bg-blue-50 text-blue-700 border-blue-200", label: "REVISION" },
};

export default function KnowledgeDetail({ itemId, open, onClose, onChanged }) {
  const [detail, setDetail] = useState(null);
  const [editing, setEditing] = useState(false);
  const [statement, setStatement] = useState("");
  const [status, setStatus] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [radar, setRadar] = useState(null);
  const [radarLoading, setRadarLoading] = useState(false);
  const [disposition, setDisposition] = useState("SAVE_REVISION");
  const [targetId, setTargetId] = useState("");

  const item = detail?.item;
  const isRadarType = item && RADAR_TYPES.includes(item.type);

  const load = useCallback(() => {
    if (!itemId) return;
    api.get(`/knowledge/${itemId}`).then((r) => {
      setDetail(r.data);
      setStatement(r.data.item.statement);
      setStatus(r.data.item.status);
      setEditing(false);
      setReason("");
      setRadar(null);
      setDisposition("SAVE_REVISION");
      setTargetId("");
    });
  }, [itemId]);

  useEffect(() => {
    if (open && itemId) load();
  }, [open, itemId, load]);

  const runRadar = async () => {
    if (!isRadarType || statement === item.statement) return;
    setRadarLoading(true);
    try {
      const { data } = await api.post(`/knowledge/${itemId}/check-edit`, { proposed_statement: statement });
      setRadar(data);
      if (data.has_conflicts) toast.warning(`Radar: ${data.summary.CONTRADICTION} contradiction(s), ${data.summary.POSSIBLE_SUPERSESSION} possible supersession(s).`);
      else toast.success("Radar: no doctrinal conflicts detected.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Radar failed");
    } finally {
      setRadarLoading(false);
    }
  };

  const save = async () => {
    if (!reason.trim()) { toast.error("A reason is required to change founder-approved knowledge."); return; }
    const stmtChanged = statement !== item.statement;
    const statusChanged = status !== item.status;
    const needsTarget = disposition === "SUPERSEDE" || disposition === "MARK_REVIEW";
    if (needsTarget && !targetId) { toast.error("Choose the target item for this disposition."); return; }
    if (!stmtChanged && !statusChanged && !needsTarget) { toast.error("Nothing to save — edit the statement/status or pick a disposition target."); return; }
    const body = {
      reason,
      change_type: disposition === "SAVE_EDITORIAL" ? "EDITORIAL" : "FOUNDER_CORRECTION",
    };
    if (stmtChanged) body.statement = statement;
    if (statusChanged) body.status = status;
    if (disposition === "SUPERSEDE") body.supersede_targets = [targetId];
    if (disposition === "MARK_REVIEW") body.review_targets = [targetId];

    setSaving(true);
    try {
      const { data } = await api.put(`/knowledge/${itemId}`, body);
      let msg = `Change recorded (${data.change_type}).`;
      if (data.superseded?.length) msg += ` Superseded ${data.superseded.join(", ")}.`;
      if (data.flagged_for_review?.length) msg += ` Flagged ${data.flagged_for_review.join(", ")} for review.`;
      toast.success(msg);
      setEditing(false);
      load();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  const conflictIds = [...new Set((radar?.conflicts || []).map((c) => c.knowledge_id))];

  return (
    <Dialog open={open} onOpenChange={(o) => { if (o) load(); else onClose(); }}>
      <DialogContent className="max-w-2xl rounded-sm bg-white max-h-[88vh] overflow-y-auto" data-testid="knowledge-detail">
        <DialogTitle className="sr-only">Knowledge item detail</DialogTitle>
        <DialogDescription className="sr-only">Inspect, edit, and view the change history of a founder-approved knowledge item.</DialogDescription>
        {item && (
          <>
            <DialogHeader>
              <div className="flex items-center gap-3">
                <span className="font-mono text-sm text-slate-700 bg-slate-100 px-2 py-0.5 rounded-sm">{item.knowledge_id}</span>
                <StatusBadge status={item.status} />
                {item.needs_review && <span className="font-mono text-[10px] uppercase text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded-sm">needs review</span>}
                {item.claim_origin && item.claim_origin !== "FOUNDER" && (
                  <span className="font-mono text-[10px] uppercase text-red-600">origin: {item.claim_origin}</span>
                )}
              </div>
              <div className="font-serif text-3xl tracking-tight text-slate-900 text-left mt-2">{item.title}</div>
            </DialogHeader>

            {!editing ? (
              <p className="text-base text-slate-700 leading-relaxed whitespace-pre-wrap">{item.statement}</p>
            ) : (
              <div className="space-y-4">
                <div>
                  <Label className="font-mono text-[11px] uppercase tracking-wider text-slate-500">Proposed Statement</Label>
                  <Textarea
                    value={statement}
                    onChange={(e) => { setStatement(e.target.value); setRadar(null); }}
                    onBlur={runRadar}
                    className="mt-1.5 rounded-sm border-slate-300 min-h-[120px]"
                    data-testid="edit-statement"
                  />
                  {isRadarType && (
                    <div className="mt-2 flex items-center gap-3">
                      <Button size="sm" variant="outline" onClick={runRadar} disabled={radarLoading || statement === item.statement} className="rounded-sm gap-1.5 border-slate-400" data-testid="run-edit-radar">
                        {radarLoading ? <Loader2 size={14} className="animate-spin" /> : <Radar size={14} />} Analyze Edit (Radar)
                      </Button>
                      <span className="text-xs text-slate-400">Runs automatically when you finish editing the statement.</span>
                    </div>
                  )}
                </div>

                {radarLoading && (
                  <div className="flex items-center gap-2 text-sm text-slate-500 border border-slate-200 bg-slate-50 px-3 py-2 rounded-sm" data-testid="edit-radar-loading">
                    <Loader2 size={14} className="animate-spin" /> Contradiction Radar analysing proposed edit…
                  </div>
                )}
                {radar && <EditRadarPanel radar={radar} />}

                <div>
                  <Label className="font-mono text-[11px] uppercase tracking-wider text-slate-500">Status</Label>
                  <Select value={status} onValueChange={setStatus}>
                    <SelectTrigger className="mt-1.5 rounded-sm border-slate-300" data-testid="edit-status"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white">{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>

                <div className="border border-slate-200 bg-slate-50 p-3 rounded-sm">
                  <Label className="font-mono text-[11px] uppercase tracking-wider text-slate-500">Disposition (you decide — nothing is overwritten automatically)</Label>
                  <Select value={disposition} onValueChange={setDisposition}>
                    <SelectTrigger className="mt-1.5 rounded-sm border-slate-300 bg-white" data-testid="edit-disposition"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white">
                      <SelectItem value="SAVE_REVISION">Create new revision (doctrinal change)</SelectItem>
                      <SelectItem value="SAVE_EDITORIAL">Save without doctrinal change (editorial)</SelectItem>
                      <SelectItem value="SUPERSEDE">Supersede an existing item</SelectItem>
                      <SelectItem value="MARK_REVIEW">Mark another item for review</SelectItem>
                    </SelectContent>
                  </Select>
                  {(disposition === "SUPERSEDE" || disposition === "MARK_REVIEW") && (
                    <div className="mt-3">
                      <Label className="font-mono text-[11px] uppercase tracking-wider text-slate-500">Target item</Label>
                      <Select value={targetId} onValueChange={setTargetId}>
                        <SelectTrigger className="mt-1.5 rounded-sm border-slate-300 bg-white" data-testid="edit-disposition-target"><SelectValue placeholder={conflictIds.length ? "Select conflicting item" : "Run radar to populate"} /></SelectTrigger>
                        <SelectContent className="bg-white">{conflictIds.map((id) => <SelectItem key={id} value={id}>{id}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  )}
                </div>

                <div>
                  <Label className="font-mono text-[11px] uppercase tracking-wider text-slate-500">Reason for change (required — recorded permanently)</Label>
                  <Textarea value={reason} onChange={(e) => setReason(e.target.value)} className="mt-1.5 rounded-sm border-slate-300" data-testid="edit-reason" />
                </div>

                <div className="flex gap-2">
                  <Button onClick={save} disabled={saving} className="rounded-sm bg-slate-900 hover:bg-slate-800" data-testid="save-change">{saving ? "Saving…" : "Apply Disposition"}</Button>
                  <Button variant="outline" onClick={() => { setEditing(false); setRadar(null); }} className="rounded-sm" data-testid="reject-edit">Reject Edit</Button>
                </div>
              </div>
            )}

            {detail.linked_decisions?.length > 0 && (
              <div className="border-t border-slate-100 pt-4">
                <div className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-wider text-slate-500 mb-2"><Link2 size={13} /> Linked Decisions</div>
                <div className="space-y-1.5">
                  {detail.linked_decisions.map((d) => (
                    <div key={d.id} className="text-sm text-slate-600"><span className="font-mono text-xs bg-slate-100 px-1.5 rounded-sm mr-2">{d.knowledge_id}</span>{d.title}</div>
                  ))}
                </div>
              </div>
            )}

            {detail.linked_sources?.length > 0 && (
              <div className="border-t border-slate-100 pt-4">
                <div className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-wider text-slate-500 mb-2"><FileText size={13} /> Linked Sources</div>
                {detail.linked_sources.map((ls, i) => ls.source && (
                  <div key={ls.link?.id || ls.source.id || `ls-${i}`} className="text-sm text-slate-600 mb-1">
                    <span className="font-mono text-xs bg-slate-100 px-1.5 rounded-sm mr-2">{ls.link.relationship}</span>{ls.source.title}
                  </div>
                ))}
              </div>
            )}

            <div className="border-t border-slate-100 pt-4">
              <div className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-wider text-slate-500 mb-2"><HistoryIcon size={13} /> Change History</div>
              {detail.history?.length ? (
                <ul className="space-y-2">
                  {detail.history.map((h) => (
                    <li key={h.id} className="text-sm text-slate-600 border-l-2 border-slate-200 pl-3">
                      <span className="font-mono text-[10px] uppercase text-slate-400">{h.change_type?.replace(/_/g, " ")}</span> · {h.reason}
                      <div className="text-xs text-slate-400 font-mono">{new Date(h.approved_at).toLocaleString()}</div>
                    </li>
                  ))}
                </ul>
              ) : <p className="text-sm text-slate-400">No modifications. Original founder-approved text.</p>}
            </div>

            {!editing && (
              <div className="border-t border-slate-100 pt-4">
                <Button variant="outline" onClick={() => setEditing(true)} className="rounded-sm" data-testid="edit-knowledge-btn">Propose Governed Change</Button>
                <p className="text-xs text-slate-400 mt-2">Editing never silently overwrites doctrine — every change is versioned, and the Radar checks it against existing knowledge first.</p>
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function EditRadarPanel({ radar }) {
  if (!radar.has_conflicts) {
    return (
      <div data-testid="edit-radar-panel" className="border border-emerald-200 bg-emerald-50 text-emerald-800 px-4 py-3 rounded-sm text-sm flex items-center gap-2">
        <Radar size={15} /> No doctrinal conflict found against current CANONICAL, WORKING, linked decisions, or open questions.
      </div>
    );
  }
  const s = radar.summary;
  return (
    <div data-testid="edit-radar-panel" className="border border-slate-300 bg-white rounded-sm">
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-slate-200 bg-slate-50">
        <AlertTriangle size={15} className="text-amber-600" />
        <span className="font-mono text-[11px] uppercase tracking-wider text-slate-600">Edit Radar — {s.CONTRADICTION} contradiction · {s.POSSIBLE_SUPERSESSION} possible supersession · {s.REVISION} revision</span>
      </div>
      <div className="px-4 py-3 space-y-3">
        {radar.conflicts.map((c) => {
          const r = REL[c.relationship_type] || REL.REVISION;
          return (
            <div key={c.knowledge_id} className="border-l-2 border-slate-300 pl-3" data-testid={`edit-conflict-${c.knowledge_id}`}>
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <span className={`inline-flex items-center border px-1.5 py-0.5 rounded-sm font-mono text-[10px] uppercase tracking-wider ${r.cls}`}>{r.label}</span>
                <span className="font-mono text-xs text-slate-700 bg-slate-100 px-1.5 rounded-sm">{c.knowledge_id}</span>
                <StatusBadge status={c.existing_status} />
                <span className="font-mono text-[10px] text-slate-400">authority {c.existing_authority} · scope {c.scope}</span>
              </div>
              <p className="text-sm text-slate-700"><span className="text-slate-400">Existing:</span> {c.existing_statement}</p>
              <p className="text-xs text-slate-600 mt-1"><span className="font-mono uppercase text-[10px] text-slate-400">Why:</span> {c.conflict_reason}</p>
            </div>
          );
        })}
        <p className="text-xs text-slate-500 pt-1 border-t border-slate-100">
          The Radar does not block or auto-resolve. Choose a disposition below: save (editorial), create a revision, supersede an item, or mark an item for review.
        </p>
      </div>
    </div>
  );
}
